"use client";

import { useTranslations } from "next-intl";
import {
  createContext,
  createElement,
  type PropsWithChildren,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { css } from "styled-system/css";

import { toast } from "@/components/toast";
import { TERMS_OF_SERVICE_URL } from "@/constants/urls";
import { env, isMainnet, isProduction } from "@/env";

import { useGeoQuery } from "../queries/geo";
import { useViewAs } from "../view-as-context";

// --- Geo restriction constants ---

const FULLY_RESTRICTED_COUNTRIES = new Set([
  "IR",
  "KP",
  "CU",
  "SY",
  "US",
  "SD",
  "SS",
  "PA", // Panama
  // US territories (separate ISO 3166-1 codes from geolocation providers)
  "PR", // Puerto Rico
  "GU", // Guam
  "VI", // US Virgin Islands
  "AS", // American Samoa
  "MP", // Northern Mariana Islands
  "UM", // US Minor Outlying Islands
]);

// Region-specific restrictions: country code -> Set of restricted region codes
const REGION_RESTRICTIONS: Record<string, Set<string>> = {
  CA: new Set(["ON"]), // Ontario, Canada
  UA: new Set(["UA-43", "UA-14", "UA-09", "43", "14", "09"]), // Crimea, Donetsk, Luhansk (with short formats)
  RU: new Set(["RU-92", "92"]), // Crimea (if detected as Russia)
};

const isCountryFullyRestricted = (country: string): boolean => {
  return FULLY_RESTRICTED_COUNTRIES.has(country);
};

const isRegionRestricted = (country: string, region: string | undefined): boolean => {
  const restrictedRegions = REGION_RESTRICTIONS[country];
  if (!restrictedRegions || !region) {
    return false;
  }
  return restrictedRegions.has(region);
};

// --- Types ---

export enum RestrictionType {
  Geo = "geo",
  ReadOnlyMode = "read_only_mode",
}

type TradingAllowedResult =
  | { allowed: true }
  | {
      allowed: false;
      title: string;
      reason: string;
      restrictionType: RestrictionType;
    };

interface TradingAllowedContextType {
  isTradingAllowed: boolean;
  restrictionType: RestrictionType | undefined;
  reason: ReactNode;
  requireTradingAllowed: (callback: () => void) => () => void;
  country: string;
  region: string | undefined;
}

// --- Pure logic ---

const getIsTradingAllowed = (
  country: string,
  region: string | undefined,
  t: ReturnType<typeof useTranslations<"toast">>,
): TradingAllowedResult => {
  // either Mainnet or overridden by env
  if ((isMainnet && isProduction) || env.NEXT_PUBLIC_TRADE_ALLOWED_CHECKS_ENABLED) {
    if (isCountryFullyRestricted(country) || isRegionRestricted(country, region)) {
      return {
        allowed: false,
        title: t("titles.restrictedRegion"),
        reason: t("summaries.geoRestriction", { region: region || country }),
        restrictionType: RestrictionType.Geo,
      };
    }
  }

  return { allowed: true };
};

// --- Context ---

const TradingAllowedContext = createContext<TradingAllowedContextType | null>(null);

export function TradingAllowedProvider({
  children,
  country: ssrCountry,
  region: ssrRegion,
}: PropsWithChildren<{ country: string; region: string | undefined }>) {
  const t = useTranslations("toast");
  const tBanner = useTranslations("warningBanner");

  const { data: geo } = useGeoQuery({ ssrCountry, ssrRegion });
  const country = geo.country;
  const region = geo.region ?? undefined;

  // --- Trading allowed ---
  const { isViewAsActive } = useViewAs();
  const result: TradingAllowedResult = useMemo(
    () =>
      isViewAsActive
        ? {
            allowed: false,
            title: t("titles.readOnlyMode"),
            reason: t("summaries.readOnlyMode"),
            restrictionType: RestrictionType.ReadOnlyMode,
          }
        : getIsTradingAllowed(country, region, t),
    [isViewAsActive, country, region, t],
  );

  const requireTradingAllowed = useCallback(
    (callback: () => void) => () => {
      if (!result.allowed) {
        toast({
          title: result.title,
          description: result.reason,
          variant: "info",
          id: result.restrictionType,
        });
        return;
      }
      callback();
    },
    [result],
  );

  const reason = useMemo((): ReactNode => {
    if (result.allowed) return undefined;
    if (result.restrictionType === RestrictionType.Geo) {
      return tBanner.rich("restrictedRegionWarning", {
        terms: (chunks) =>
          createElement(
            "a",
            {
              href: TERMS_OF_SERVICE_URL,
              target: "_blank",
              rel: "noopener noreferrer",
              className: css({
                color: "[inherit]",
                textDecoration: "underline",
                _hover: { opacity: 0.8 },
              }),
            },
            chunks,
          ),
      });
    }
    return result.reason;
  }, [result, tBanner]);

  const value = useMemo<TradingAllowedContextType>(
    () => ({
      isTradingAllowed: result.allowed,
      restrictionType: result.allowed ? undefined : result.restrictionType,
      reason,
      requireTradingAllowed,
      country,
      region,
    }),
    [result, reason, requireTradingAllowed, country, region],
  );

  return <TradingAllowedContext.Provider value={value}>{children}</TradingAllowedContext.Provider>;
}

export function useTradingAllowed() {
  const ctx = useContext(TradingAllowedContext);
  if (!ctx) {
    throw new Error("useTradingAllowed must be used inside TradingAllowedProvider");
  }
  return ctx;
}

export function useRequireTradingAllowed() {
  return useTradingAllowed().requireTradingAllowed;
}

export function useCountry() {
  const { country, region } = useTradingAllowed();
  return { country, region };
}
